import { memo, useMemo } from 'react';

import { type Board } from '@air/api/types';
import { type JustifiedBox } from '@air/component-gallery-view';

import BoardThumbnail, { type BoardThumbnailProps } from '~/components/BoardThumbnail/BoardThumbnail';
import { getPreferenceImgixFit, useThumbnailPreference } from '~/hooks/useThumbnailPreference';

export type GalleryBoardThumbnailProps = Pick<BoardThumbnailProps, 'board' | 'sizes'> & {
  board: Pick<Board, 'id'>;
  box: Pick<JustifiedBox, 'height' | 'width'>;
  onFallbackChange?: (isFallback: boolean) => void;
};

export const GalleryBoardThumbnail = memo(({ board, box, sizes, onFallbackChange }: GalleryBoardThumbnailProps) => {
  const { getPreference } = useThumbnailPreference();

  const thumbnailPreference = useMemo(() => getPreference(board.id), [board.id, getPreference]);

  return (
    // The clip radius reads the --gallery-card-inner-radius var (set by the public collection
    // page's Corner rounding setting — see shareLinkThumbnailRadius.ts) and falls back to the
    // native 8px, so every surface that doesn't set the var renders exactly as before.
    <div className="pointer-events-none absolute inset-1 overflow-hidden rounded-[var(--gallery-card-inner-radius,8px)]">
      <div className="absolute inset-0 z-10 hidden size-full bg-black/10 group-data-[valid-thumbnail=true]/gallery-selectable-card:block" />
      <BoardThumbnail
        board={board}
        // The image draws its own corners, so the clip above can't square them off — the
        // thumbnail's radius must follow the same var, with the exact native 4px fallback.
        borderRadius="var(--gallery-card-inner-radius, 4px)"
        height="100%"
        imgixParams={{
          fit: getPreferenceImgixFit(thumbnailPreference),
          h: box.height,
          w: box.width,
        }}
        imgixHeight={box.height}
        imgixWidth={box.width}
        objectFit={thumbnailPreference === 'default' ? 'cover' : 'contain'}
        sizes={sizes}
        width="100%"
        onFallbackChange={onFallbackChange}
      />
    </div>
  );
});

GalleryBoardThumbnail.displayName = 'GalleryBoardThumbnail';
